import { useEffect, useState } from "react";
import { Minus, Plus, Trash2, Receipt as ReceiptIcon } from "lucide-react";
import { formatMoney } from "@finora/config";
import { Button, EmptyState } from "@finora/ui";
import type { FiscalDocumentDto } from "@finora/types";
import { BuyerSection } from "./BuyerSection";
import { AdvanceInvoiceSection, type CreateAdvanceSaleFormInput } from "./AdvanceInvoiceSection";

export interface CartProps {
  document: FiscalDocumentDto | null;
  onIncrement: (itemId: string, currentQuantity: string) => void;
  onDecrement: (itemId: string, currentQuantity: string) => void;
  onRemove: (itemId: string) => void;
  onClear: () => void;
  onCheckout: () => void;
  onCreateAdvanceSale: (input: CreateAdvanceSaleFormInput) => void;
  isCreatingAdvanceSale?: boolean;
  createAdvanceSaleError?: string | null;
  // Popust po stavci (24.09.2026, korisnički zahtev) - backend polje
  // (item.discountAmount) postoji od PDV/popust zakrpe, ova kasa ga do sada
  // nije nudila za unos, samo prikaz na nivou dokumenta.
  onUpdateItemDiscount: (itemId: string, discountAmount: string) => void;
  // Ručni unos količine (28.09.2026, korisnički zahtev - vidi
  // ItemQuantityInput ispod).
  onSetQuantity: (itemId: string, quantity: string) => void;
}

// Malo, izolovano polje za popust po stavci - UNOSI SE U PROCENTIMA
// (24.09.2026, korisnički zahtev: "popust u % ne u iznosu"), drži
// sopstveni "draft" tekst dok kasir kuca, i tek na blur konvertuje % u
// apsolutan iznos i šalje ga na server (backend polje `discountAmount`
// ostaje apsolutan iznos, konverzija je isključivo na kasi). Osnovica za
// % je bruto vrednost stavke PRE popusta (količina × jed. cena) - ista
// osnovica koju calculateItem() koristi za oduzimanje popusta.
function ItemDiscountInput({
  discountAmount,
  baseAmount,
  onCommitAmount,
}: {
  discountAmount: string;
  baseAmount: number;
  onCommitAmount: (amount: string) => void;
}) {
  const percentFromAmount = (amount: string) => {
    const amt = Number(amount);
    if (baseAmount <= 0 || amt <= 0) return "";
    return (amt / baseAmount) * 100;
  };
  const toDisplay = (pct: number | "") => (pct === "" ? "" : pct.toFixed(pct % 1 === 0 ? 0 : 2));

  const [draft, setDraft] = useState(() => toDisplay(percentFromAmount(discountAmount)));

  useEffect(() => {
    setDraft(toDisplay(percentFromAmount(discountAmount)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountAmount, baseAmount]);

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label="Popust na stavku (%)"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const pct = draft.trim() === "" ? 0 : Number(draft.replace(",", "."));
        const clampedPct = Math.min(Math.max(Number.isFinite(pct) ? pct : 0, 0), 100);
        const nextAmount = ((clampedPct / 100) * baseAmount).toFixed(2);
        if (nextAmount !== Number(discountAmount).toFixed(2)) onCommitAmount(nextAmount);
      }}
      placeholder="%"
      className="h-8 w-14 rounded-md border border-border px-1.5 text-center text-xs text-amber-700 focus:border-brand focus:outline-none"
    />
  );
}

// Ručni unos količine (28.09.2026, korisnički zahtev: "unos količine ne
// reaguje na zarez/tačku, ostaju samo celi brojevi") - količina do sada
// NIJE imala tekstualno polje, samo +/- dugmad koja menjaju za tačno 1,
// pa nije postojao način da se direktnim kucanjem unese razlomljena
// količina (npr. 1.5 m, 0.5 kg). Isti obrazac kao ItemDiscountInput iznad:
// sopstveni "draft" tekst dok kasir kuca, commit tek na blur. Prihvata i
// zarez i tačku kao decimalni separator (kucanje ide sa zarezom kako je
// uobičajeno kod nas) - backend (quantitySchema u @finora/validation)
// očekuje tačku, pa se zarez konvertuje pre slanja.
function ItemQuantityInput({
  quantity,
  onCommit,
}: {
  quantity: string;
  onCommit: (quantity: string) => void;
}) {
  const [draft, setDraft] = useState(quantity);

  useEffect(() => {
    setDraft(quantity);
  }, [quantity]);

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label="Količina"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const normalized = draft.trim().replace(",", ".");
        const qty = Number(normalized);
        if (!Number.isFinite(qty) || qty <= 0) {
          // Neispravan unos (prazno, slova, 0 ili negativno) - vrati na
          // poslednju važeću količinu umesto slanja na server.
          setDraft(quantity);
          return;
        }
        const nextQuantity = qty.toString();
        if (nextQuantity !== quantity) onCommit(nextQuantity);
        else setDraft(quantity);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      className="h-8 w-14 rounded-md border border-border text-center text-sm font-medium focus:border-brand focus:outline-none"
    />
  );
}

export function Cart({
  document: doc,
  onIncrement,
  onDecrement,
  onRemove,
  onClear,
  onCheckout,
  onCreateAdvanceSale,
  isCreatingAdvanceSale,
  createAdvanceSaleError,
  onUpdateItemDiscount,
  onSetQuantity,
}: CartProps) {
  const hasItems = !!doc && doc.items.length > 0;
  // Doplata (razgovor 23.09.2026) - dokument nastao zatvaranjem avansa nosi
  // advanceCreditAmount (računsko polje, vidi FiscalDocumentsService.get()).
  const advanceCredit = Number(doc?.advanceCreditAmount ?? "0");
  const dueAfterAdvance = doc ? Math.max(Number(doc.grossAmount) - advanceCredit, 0) : 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Novi račun</h2>
          <p className="text-xs text-slate-400">
            {doc ? `${new Date(doc.createdAt).toLocaleTimeString("sr-RS", { hour: "2-digit", minute: "2-digit" })}` : "—"}
          </p>
        </div>
        {hasItems && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-red-600 hover:underline"
          >
            Isprazni
          </button>
        )}
      </div>

      {doc && <BuyerSection document={doc} />}
      <AdvanceInvoiceSection
        document={doc}
        onCreateAdvanceSale={onCreateAdvanceSale}
        isCreatingAdvanceSale={isCreatingAdvanceSale}
        createAdvanceSaleError={createAdvanceSaleError}
      />

      {/* min-h-0 (24.09.2026, nastavak skrol ispravke) - isti razlog kao u
          ProductGrid.tsx: ovaj div je flex-stavka unutar "flex h-full
          flex-col" roditelja, treba mu min-h-0 da overflow-auto stvarno
          ograniči korpu umesto da je pusti da se širi na punu visinu. */}
      <div className="min-h-0 flex-1 overflow-auto py-3">
        {!hasItems ? (
          <EmptyState icon={ReceiptIcon} title="Račun je prazan" description="Kliknite na artikal levo da ga dodate." />
        ) : (
          <ul className="space-y-3">
            {doc!.items.map((item) => (
              <li key={item.id} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{item.name}</p>
                    <p className="text-xs text-slate-400">
                      {item.quantity} × {formatMoney(item.unitPriceGross)} · PDV {item.taxRatePercent}%
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{formatMoney(item.grossAmount)}</p>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Smanji količinu"
                    onClick={() => onDecrement(item.id, item.quantity)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-slate-600 hover:bg-slate-50"
                  >
                    <Minus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  <ItemQuantityInput
                    quantity={item.quantity}
                    onCommit={(quantity) => onSetQuantity(item.id, quantity)}
                  />
                  <button
                    type="button"
                    aria-label="Povećaj količinu"
                    onClick={() => onIncrement(item.id, item.quantity)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-slate-600 hover:bg-slate-50"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  <ItemDiscountInput
                    discountAmount={item.discountAmount}
                    baseAmount={Number(item.quantity) * Number(item.unitPriceGross)}
                    onCommitAmount={(discountAmount) => onUpdateItemDiscount(item.id, discountAmount)}
                  />
                  <button
                    type="button"
                    aria-label="Ukloni stavku"
                    onClick={() => onRemove(item.id)}
                    className="ml-auto flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2 border-t border-border pt-3">
        <div className="flex justify-between text-sm text-slate-500">
          <span>Osnovica</span>
          <span>{formatMoney(doc?.netAmount ?? "0")}</span>
        </div>
        <div className="flex justify-between text-sm text-slate-500">
          <span>PDV</span>
          <span>{formatMoney(doc?.taxAmount ?? "0")}</span>
        </div>
        {Number(doc?.discountAmount ?? "0") > 0 && (
          <div className="flex justify-between text-sm text-amber-600">
            <span>Popust</span>
            <span>-{formatMoney(doc!.discountAmount)}</span>
          </div>
        )}
        {advanceCredit > 0 && (
          <>
            <div className="flex justify-between text-sm text-slate-500">
              <span>Ukupan promet</span>
              <span>{formatMoney(doc?.grossAmount ?? "0")}</span>
            </div>
            <div className="flex justify-between text-sm text-emerald-600">
              <span>Plaćeno avansom</span>
              <span>-{formatMoney(String(advanceCredit))}</span>
            </div>
          </>
        )}
        <div className="flex justify-between text-base font-semibold text-slate-900">
          <span>{advanceCredit > 0 ? "Preostalo za uplatu" : "Za plaćanje"}</span>
          <span>{formatMoney(String(dueAfterAdvance))}</span>
        </div>
        <Button
          size="lg"
          variant="success"
          className="w-full"
          disabled={!hasItems}
          onClick={onCheckout}
        >
          Naplati račun (F9)
        </Button>
      </div>
    </div>
  );
}
